import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus,
  MagnifyingGlass as Search,
  IdentificationCard as UserCheck,
  Envelope as Mail,
  Phone,
  CircleNotch as Spinner,
  UserMinus,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatCard } from '@/components/shared/StatCard';
import { Avatar } from '@/components/ui/avatar';
import { RowActions } from '@/components/shared/RowActions';
import { accountStatusLabel, memberAccountAction } from '@/lib/accountLifecycle';
import { useAuthStore } from '@/features/auth/store';
import { fetchUsers, updateUserStatus, type ApiUser } from '@/lib/usersApi';
import { InviteMemberModal } from '@/components/shared/InviteMemberModal';

const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin',
  senior_clerk: 'Senior clerk',
  junior_clerk: 'Junior clerk',
  staff: 'Staff',
};

export default function ClerksPage() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Who is looking — Deactivate is offered only where this person may actually do it (never on
  // their own row, never beyond their authority), so the button never dead-ends on a server refusal.
  const me = useAuthStore((st) => st.user);

  const loadStaff = () =>
    fetchUsers({ limit: 200 }) // lift the default 100-row cap so search covers all staff
      .then((users) => {
        const clerksAndStaff = users.filter(
          (u) => u.memberType === 'admin' || u.memberType === 'senior_clerk' || u.memberType === 'junior_clerk' || u.memberType === 'staff'
        );
        setStaff(clerksAndStaff);
      })
      .finally(() => setLoading(false));

  useEffect(() => {
    // Fetch clerks and staff (not barristers or solicitors)
    loadStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeactivate = async (userId: string, name: string) => {
    try {
      // Deactivate means deactivate (status 'inactive', recorded server-side) — reversible from
      // Settings → Manage members, where a deactivated member offers Reactivate.
      await updateUserStatus(userId, 'inactive');
      await loadStaff();
      toast.success(`${name} deactivated`);
    } catch {
      toast.error('Could not deactivate this person');
    }
  };

  const filteredStaff = useMemo(() => {
    if (!searchQuery.trim()) return staff;
    const q = searchQuery.toLowerCase();
    return staff.filter(
      (s) =>
        `${s.firstName} ${s.lastName}`.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        s.memberType.toLowerCase().includes(q)
    );
  }, [staff, searchQuery]);

  // Compute stats from real data
  const stats = useMemo(() => {
    const senior = staff.filter((s) => s.memberType === 'senior_clerk').length;
    const junior = staff.filter((s) => s.memberType === 'junior_clerk').length;
    const other = staff.filter((s) => s.memberType === 'staff' || s.memberType === 'admin').length;
    const active = staff.filter((s) => s.status === 'active').length;
    const inactive = staff.filter((s) => s.status !== 'active').length;
    return { total: staff.length, senior, junior, other, active, inactive };
  }, [staff]);

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Clerks & Staff"
        subtitle="Senior clerks, junior clerks and non-fee-earning staff"
        actions={
          <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setInviteOpen(true)}>
            Add staff
          </Button>
        }
      />
      <InviteMemberModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        defaultMemberType="senior_clerk"
        onSuccess={() => { void loadStaff(); }}
      />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          label="Total staff"
          value={loading ? '...' : String(stats.total)}
          hint={loading ? '' : `${stats.senior} senior · ${stats.junior} junior · ${stats.other} other`}
        />
        <StatCard label="Active" value={loading ? '...' : String(stats.active)} />
        <StatCard label="Not active" value={loading ? '...' : String(stats.inactive)} />
        <StatCard label="Roles" value={loading ? '...' : '4'} hint="admin · senior · junior · staff" />
      </div>

      <Card className="mb-4 p-3">
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search staff by name, email or role…"
            className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </Card>

      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredStaff.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            {searchQuery ? 'No staff matching your search' : 'No clerks or staff found'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Job Title</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th className="w-10 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
            <tbody>
              {filteredStaff.map((s) => {
                const name = `${s.firstName} ${s.lastName}`;
                const initials = `${s.firstName?.[0] ?? ''}${s.lastName?.[0] ?? ''}`;
                return (
                  <tr key={s.id} className="cursor-pointer">
                    <td className="font-medium text-foreground">
                      <div className="flex items-center gap-2.5">
                        <Avatar fallback={initials} size="sm" />
                        {name}
                      </div>
                    </td>
                    <td>
                      <Badge variant={s.memberType === 'senior_clerk' ? 'info' : 'default'}>
                        <UserCheck className="h-3 w-3" />
                        {ROLE_LABEL[s.memberType] ?? s.memberType}
                      </Badge>
                    </td>
                    <td className="text-muted-foreground">{s.jobTitle ?? '—'}</td>
                    <td className="text-muted-foreground">
                      <Mail className="mr-1 inline h-3 w-3" />
                      {s.email}
                    </td>
                    <td className="text-xs text-muted-foreground">
                      {s.phone ? (
                        <>
                          <Phone className="mr-1 inline h-3 w-3" />
                          {s.phone}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <Badge variant={s.status === 'active' ? 'success' : 'warning'}>
                        {accountStatusLabel(s.status)}
                      </Badge>
                    </td>
                    <td>
                      <RowActions
                        onDelete={memberAccountAction(me, s)?.kind === 'suspend' && s.status === 'active' ? () => handleDeactivate(s.id, name) : undefined}
                        deleteLabel="Deactivate"
                        deleteIcon={<UserMinus className="h-4 w-4" />}
                        deleteTone="neutral"
                        deleteConfirm={{
                          title: `Deactivate ${name}?`,
                          message: `${name} will lose access immediately. Their account and records are kept, and they can be reactivated from Settings → Manage members.`,
                          confirmLabel: 'Deactivate',
                        }}
                      />
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
